import { Mesh, MeshLayoutSegment } from '../mesh';
import { Material } from '../material';
import { RenderObject } from '../object/RenderObject';
import { RenderGroup } from '../object/RenderGroup';
import { GBufferShaderSettings } from '../shader/generated';
import { ReadonlyVec3 } from 'gl-matrix';
import type { PrepareStats } from './InstanceManager';

const FLOATS_PER_INSTANCE = 16; // mat4 = 4x4

export type LodLevel = {
  mesh: Mesh;
  maxDistanceSq: number; // squared distance for fast comparison
  doubleSided?: boolean; // e.g. billboards need both faces
  material?: Material; // override material for this LOD (e.g. impostor texture)
  /**
   * Crossfade band as a fraction of the threshold distance. Clamped at register
   * time so adjacent fade bands cannot overlap (half-gap to the previous level).
   */
  fadeBand: number;
  /** True for cross-billboard impostor LODs — excluded from depth/shadow passes. */
  isBillboard?: boolean;
};

/**
 * A group of RenderObjects sharing the same mesh and material that can be
 * drawn in a single instanced draw call.
 *
 * When LOD levels are registered, visible members are partitioned by distance
 * from the camera.  LOD 0 (the base mesh) covers [0, lod[0].maxDistanceSq).
 * Each subsequent level covers the next distance band.  Each LOD is drawn
 * with its own VAO (different vertex data, same instance attribute layout).
 */
export class InstanceGroup {
  /**
   * Legacy RenderObject members (authored static placements: Sponza props,
   * etc). Per-instance LOD partition + per-instance crossfade — same path
   * the renderer has always used.
   */
  public members: RenderObject[] = [];
  public visibleMembers: RenderObject[] = [];

  /**
   * RenderGroup members (scatter clusters). Each RenderGroup carries
   * pre-baked instance matrices for an entire scatter tile-mesh pair.
   * Per-cluster LOD: every instance inside one RenderGroup uses the same
   * LOD level, picked once per frame from the tile center's distance to the
   * camera.
   *
   * Crossfade is per-cluster: when a RenderGroup's tile-center distance
   * falls in a LOD boundary's fade band, the entire group is drawn twice
   * (current LOD + next LOD) with complementary dither — same machinery
   * `drawTransition` already uses for per-instance authored statics. The
   * fade factor used for the dither is averaged across all transition
   * members in a bucket (RG fades + per-instance RO fades), weighted by
   * instance count; close-enough to per-RG values for the dither cell.
   */
  public renderGroupMembers: Set<RenderGroup> = new Set();
  public visibleRenderGroups: RenderGroup[] = [];

  /** Per-LOD visible counts after the last updateAndUpload(). */
  public lodVisibleCounts: number[] = [];

  /**
   * When set, instances at squared distance >= this value (before lodDistanceScale)
   * are excluded from all buckets and never rendered. Set from a skip LOD tier.
   */
  public skipDistanceSq: number | null = null;

  /**
   * When false, this group is excluded from all depth/shadow passes. Used to
   * suppress far-billboard groups (whose base mesh IS the cross-billboard) from
   * casting cardboard-shaped shadows.
   */
  public castsShadows = true;

  /**
   * When true, the base mesh is drawn with backface culling disabled. Set on
   * far-billboard InstanceGroups whose base mesh IS the CrossBillboardMesh —
   * unlike near-billboard LOD levels (which set doubleSided on the LodLevel),
   * the far path has no LOD levels so doubleSided must live here.
   */
  public baseDoubleSided = false;

  /**
   * Per LOD boundary: number of instances in the transition zone and their
   * average fade factor.  `lodTransitions[i]` describes the LOD i → i+1
   * boundary.  Transition instances are the LAST entries in bucket i.
   */
  public lodTransitions: { count: number; avgFade: number }[] = [];

  private instanceVAO: WebGLVertexArrayObject | null = null;
  private instanceBuffer: WebGLBuffer | null = null;
  private instanceData: Float32Array | null = null;
  private capacity = 0;

  // LOD levels (sorted by maxDistanceSq ascending).
  // LOD 0 is always the base mesh; entries here start from LOD 1.
  private lodLevels: LodLevel[] = [];

  /** Number of registered LOD levels (not counting the base mesh). */
  public get lodLevelCount(): number {
    return this.lodLevels.length;
  }
  private lodVAOs: WebGLVertexArrayObject[] = [];

  /**
   * Optional dedicated mesh used by shadow passes (`useShadowLod`).
   * Decoupled from the camera-distance LOD partition because the simplest
   * decimated mesh isn't always registered as a LOD level — e.g. when
   * `lodInfo.maxDistance` exceeds a billboard's takeover distance, the
   * registrar skips registering it (would invert sort order) but still
   * keeps it available here for shadow casting.
   */
  private shadowMesh: Mesh | null = null;
  private shadowMeshVAO: WebGLVertexArrayObject | null = null;

  // Pre-allocated per-frame scratch for LOD partitioning (sized on registerLod)
  private hardBuckets: RenderObject[][] = [];
  private coreBuckets: RenderObject[][] = [];
  private transitionBuckets: RenderObject[][] = [];
  private transitionFadeSums: number[] = [];
  // Parallel scratch for RenderGroup buckets — sized lazily on first use
  // because RG support was added later; resized in `groupBucketsScratch`.
  private groupBuckets: RenderGroup[][] = [];
  // Crossfade-only: RenderGroups whose tile-center sits in a LOD boundary's
  // fade band. Drawn at the end of bucket i with the LOD i mesh, and again
  // with the LOD i+1 mesh at complementary dither — `drawTransition` reuses
  // the existing offset math via `lodTransitions[i].count`.
  private groupTransitionBuckets: RenderGroup[][] = [];
  // Sum of fade factors for transition RenderGroups, weighted by instance
  // count, used to compute the per-bucket avgFade alongside the per-instance
  // RenderObject contributions.
  private groupTransitionFadeSums: number[] = [];
  // Cached crossfade thresholds — recomputed only when scale changes
  private cachedLodScale = -1;
  private cachedThresholds: number[] = [];
  private cachedFadeBands: number[] = [];

  constructor(
    public readonly key: string,
    public readonly mesh: Mesh,
    public readonly material: Material | undefined,
  ) {}

  /** Register an additional LOD level (lower-detail mesh at greater distance). */
  public registerLod(
    mesh: Mesh,
    maxDistance: number,
    doubleSided = false,
    material?: Material,
    fadeBand = 0.1,
    isBillboard = false,
  ): void {
    this.lodLevels.push({
      mesh,
      maxDistanceSq: maxDistance * maxDistance,
      doubleSided,
      material,
      fadeBand,
      isBillboard,
    });
    // Keep sorted by distance ascending
    this.lodLevels.sort((a, b) => a.maxDistanceSq - b.maxDistanceSq);
    // After sorting, clamp each level's fadeBand to half the gap to the previous
    // threshold so adjacent fade ranges can never overlap.
    for (let i = 0; i < this.lodLevels.length; i++) {
      const t = Math.sqrt(this.lodLevels[i].maxDistanceSq);
      const prevT = i === 0 ? 0 : Math.sqrt(this.lodLevels[i - 1].maxDistanceSq);
      const halfGapFraction = (t - prevT) / (2 * t);
      if (this.lodLevels[i].fadeBand > halfGapFraction) {
        this.lodLevels[i].fadeBand = halfGapFraction;
      }
    }
    this.resizeLodBuckets();
  }

  /**
   * Register a dedicated mesh for shadow casting. Used by shadow passes
   * (via `drawAllAtShadowMesh`) to render every visible instance with this
   * mesh, bypassing the camera-distance LOD partition. Last write wins.
   * The VAO is built lazily in `buildInstanceVAO` (or rebuilt on resize).
   */
  public setShadowMesh(mesh: Mesh): void {
    this.shadowMesh = mesh;
    // VAO is built (or rebuilt) the next time buildInstanceVAO/rebuildVAOs runs.
    this.shadowMeshVAO = null;
  }

  /** Whether a shadow mesh has been registered. */
  public get hasShadowMesh(): boolean {
    return this.shadowMesh !== null;
  }

  /** True when the LOD at `index` is a cross-billboard impostor (excluded from depth/shadow passes). */
  public isLodBillboard(index: number): boolean {
    return this.lodLevels[index]?.isBillboard ?? false;
  }

  private resizeLodBuckets(): void {
    const bucketCount = this.lodLevels.length + 1;
    this.hardBuckets = new Array(bucketCount);
    this.coreBuckets = new Array(bucketCount);
    this.transitionBuckets = new Array(this.lodLevels.length);
    this.transitionFadeSums = new Array(this.lodLevels.length);
    this.cachedThresholds = new Array(this.lodLevels.length);
    this.cachedFadeBands = new Array(this.lodLevels.length);
    for (let i = 0; i < bucketCount; i++) {
      this.hardBuckets[i] = [];
      this.coreBuckets[i] = [];
    }
    for (let i = 0; i < this.lodLevels.length; i++) {
      this.transitionBuckets[i] = [];
    }
    // Pre-size output arrays so updateAndUpload() can write by index without allocating.
    this.lodVisibleCounts = new Array(bucketCount).fill(0);
    this.lodTransitions = this.lodLevels.map(() => ({ count: 0, avgFade: 0 }));
    this.cachedLodScale = -1; // force threshold recompute
  }

  public get hasLods(): boolean {
    return this.lodLevels.length > 0;
  }

  /**
   * Returns the GBufferShaderSettings for this group (INSTANCED + texture/color flags).
   */
  public getGBufferSettings(): GBufferShaderSettings {
    const settings: GBufferShaderSettings = ['INSTANCED'];
    if (this.material?.baseColor && this.mesh.dataLayout.diffuseTexture) {
      settings.push('TEXTURE');
      if (this.material.normalMap) settings.push('NORMAL_MAP');
      if (this.material.metallicRoughnessMap) settings.push('METALLIC_ROUGHNESS');
    } else if (this.mesh.dataLayout.color) {
      settings.push('ATTRIBUTE_COLOR', 'PRESET_COLOR');
    } else {
      settings.push('PRESET_COLOR');
    }
    return settings;
  }

  /**
   * Returns the GBufferShaderSettings for a LOD level.  LOD meshes may have
   * fewer attributes (e.g. no normal map) so we derive settings per-LOD.
   */
  public getLodGBufferSettings(lodIndex: number): GBufferShaderSettings {
    const lod = this.lodLevels[lodIndex];
    const lodMaterial = lod.material ?? this.material;
    const settings: GBufferShaderSettings = ['INSTANCED'];
    if (lodMaterial?.baseColor && lod.mesh.dataLayout.diffuseTexture) {
      settings.push('TEXTURE');
      // LOD meshes intentionally skip normal/metallic-roughness maps for perf
    } else if (lod.mesh.dataLayout.color) {
      settings.push('ATTRIBUTE_COLOR', 'PRESET_COLOR');
    } else {
      settings.push('PRESET_COLOR');
    }
    return settings;
  }

  /** Get the effective material for a LOD level (may override the group material). */
  public getLodMaterial(lodIndex: number): Material | undefined {
    return this.lodLevels[lodIndex].material ?? this.material;
  }

  public addMember(obj: RenderObject): void {
    this.members.push(obj);
  }

  public removeMember(obj: RenderObject): void {
    const idx = this.members.indexOf(obj);
    if (idx !== -1) this.members.splice(idx, 1);
  }

  /** Add a RenderGroup cluster. Mutually-exclusive set lookup; idempotent. */
  public addGroupMember(group: RenderGroup): void {
    this.renderGroupMembers.add(group);
  }

  /** Remove a RenderGroup cluster. */
  public removeGroupMember(group: RenderGroup): void {
    this.renderGroupMembers.delete(group);
  }

  /**
   * Build the instance VAO: re-binds mesh attributes at locations 0-5,
   * then adds instance model matrix attributes at locations 6-9.
   */
  public buildInstanceVAO(gl: WebGL2RenderingContext): void {
    if (this.instanceVAO) return;

    // Create shared instance buffer
    this.instanceBuffer = gl.createBuffer()!;
    this.capacity = Math.max(this.members.length, 16);
    this.instanceData = new Float32Array(this.capacity * FLOATS_PER_INSTANCE);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      this.capacity * FLOATS_PER_INSTANCE * Float32Array.BYTES_PER_ELEMENT,
      gl.DYNAMIC_DRAW,
    );

    // Build VAO for LOD 0 (base mesh)
    this.instanceVAO = this.buildVAOForMesh(gl, this.mesh);

    // Build VAOs for additional LOD levels
    for (const lod of this.lodLevels) {
      this.lodVAOs.push(this.buildVAOForMesh(gl, lod.mesh));
    }

    if (this.shadowMesh) {
      this.shadowMeshVAO = this.buildVAOForMesh(gl, this.shadowMesh);
    }
  }

  private buildVAOForMesh(gl: WebGL2RenderingContext, mesh: Mesh): WebGLVertexArrayObject {
    const vao = gl.createVertexArray()!;
    gl.bindVertexArray(vao);

    // Bind mesh vertex buffer with attribute layout 0-5
    gl.bindBuffer(gl.ARRAY_BUFFER, mesh.buffer.glVertexBuffer);
    const stride = mesh.buffer.itemSize * Float32Array.BYTES_PER_ELEMENT;

    const layout = mesh.dataLayout;
    if (layout.position) this.bindAttr(gl, 0, layout.position, stride);
    if (layout.normal) this.bindAttr(gl, 1, layout.normal, stride);
    if (layout.diffuseTexture) this.bindAttr(gl, 2, layout.diffuseTexture, stride);
    if (layout.color) this.bindAttr(gl, 3, layout.color, stride);
    if (layout.jointWeights) this.bindAttr(gl, 4, layout.jointWeights, stride);
    if (layout.jointIndices) this.bindAttr(gl, 5, layout.jointIndices, stride);

    // Bind shared instance buffer for model matrix at locations 6-9
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    const instStride = FLOATS_PER_INSTANCE * Float32Array.BYTES_PER_ELEMENT;
    for (let col = 0; col < 4; col++) {
      const loc = 6 + col;
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(
        loc,
        4,
        gl.FLOAT,
        false,
        instStride,
        col * 4 * Float32Array.BYTES_PER_ELEMENT,
      );
      gl.vertexAttribDivisor(loc, 1);
    }

    // Bind index buffer
    if (mesh.indexBuffer) {
      gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, mesh.indexBuffer.glVertexBuffer);
    }

    gl.bindVertexArray(null);
    return vao;
  }

  /**
   * Update visible set and upload instance transforms to GPU.
   * When LOD levels exist, members are partitioned by distance from camera.
   */
  public updateAndUpload(
    gl: WebGL2RenderingContext,
    visible: RenderObject[],
    visibleGroups: RenderGroup[] = [],
    cameraPosition?: ReadonlyVec3,
    lodDistanceScale = 1.0,
    crossfadeEnabled = false,
    stats?: PrepareStats,
  ): void {
    this.visibleMembers = visible;
    this.visibleRenderGroups = visibleGroups;

    // Group instances total — sum here once, used everywhere below.
    let groupInstanceCount = 0;
    for (const g of visibleGroups) groupInstanceCount += g.instanceCount;

    const total = visible.length + groupInstanceCount;
    if (total === 0) {
      this.lodVisibleCounts.length = 0;
      return;
    }

    // Grow buffer if needed
    if (total > this.capacity) {
      const growStart = stats ? performance.now() : 0;
      this.capacity = Math.ceil(total * 1.5);
      this.instanceData = new Float32Array(this.capacity * FLOATS_PER_INSTANCE);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
      gl.bufferData(
        gl.ARRAY_BUFFER,
        this.capacity * FLOATS_PER_INSTANCE * Float32Array.BYTES_PER_ELEMENT,
        gl.DYNAMIC_DRAW,
      );
      // Rebuild all VAOs since the buffer was recreated
      this.rebuildVAOs(gl);
      if (stats) stats.bufferGrowMs += performance.now() - growStart;
    }

    if (this.lodLevels.length === 0 || !cameraPosition) {
      // No LODs — upload all visible RenderGroup matrices first (one bulk
      // copy per RenderGroup), then RenderObject matrices, single bufferSubData.
      this.lodVisibleCounts[0] = total;
      this.lodVisibleCounts.length = 1;
      const copyStart = stats ? performance.now() : 0;
      const data = this.instanceData!;
      let offset = 0;
      for (const g of visibleGroups) {
        // Bulk copy: one Float32Array.set replaces what would have been
        // `instanceCount` per-instance writeMatrices calls.
        data.set(g.instanceMatrices, offset * FLOATS_PER_INSTANCE);
        offset += g.instanceCount;
      }
      this.writeMatrices(data, visible, offset);
      if (stats) stats.matrixCopyMs += performance.now() - copyStart;
      const uploadStart = stats ? performance.now() : 0;
      gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, data, 0, total * FLOATS_PER_INSTANCE);
      if (stats) stats.uploadMs += performance.now() - uploadStart;
      return;
    }

    const bucketCount = this.lodLevels.length + 1;
    const scaleSq = lodDistanceScale * lodDistanceScale;
    const cx = cameraPosition[0];
    const cy = cameraPosition[1];
    const cz = cameraPosition[2];

    if (!crossfadeEnabled) {
      // Simple hard-threshold partitioning (no transition bands).
      // Layout per bucket: [RenderGroup matrices...][RenderObject matrices...]
      const buckets = this.hardBuckets;
      const groupBuckets = this.groupBucketsScratch(bucketCount);
      for (let i = 0; i < bucketCount; i++) {
        buckets[i].length = 0;
        groupBuckets[i].length = 0;
      }

      const partStart = stats ? performance.now() : 0;
      // RenderGroup partition: per-cluster LOD by tile-center distance.
      // O(visibleGroups), which is ~50 even for 100K scatter — replaces the
      // 38K per-instance distance test in the old per-instance LOD path.
      const skipSq = this.skipDistanceSq;
      for (const g of visibleGroups) {
        const tc = g.tileCenter;
        const dx = tc[0] - cx;
        const dy = tc[1] - cy;
        const dz = tc[2] - cz;
        const distSq = dx * dx + dy * dy + dz * dz;
        if (skipSq !== null && distSq >= skipSq * scaleSq) continue;
        let bucket = bucketCount - 1;
        for (let i = 0; i < this.lodLevels.length; i++) {
          if (distSq < this.lodLevels[i].maxDistanceSq * scaleSq) {
            bucket = i;
            break;
          }
        }
        groupBuckets[bucket].push(g);
      }
      // RenderObject partition: per-instance LOD (legacy authored statics).
      for (const obj of visible) {
        const pos = obj.bounds!.boundingSphere.position;
        const dx = pos[0] - cx;
        const dy = pos[1] - cy;
        const dz = pos[2] - cz;
        const distSq = dx * dx + dy * dy + dz * dz;
        if (skipSq !== null && distSq >= skipSq * scaleSq) continue;
        let bucket = bucketCount - 1;
        for (let i = 0; i < this.lodLevels.length; i++) {
          if (distSq < this.lodLevels[i].maxDistanceSq * scaleSq) {
            bucket = i;
            break;
          }
        }
        buckets[bucket].push(obj);
      }
      if (stats) stats.lodPartitionMs += performance.now() - partStart;

      const data = this.instanceData!;
      let offset = 0;
      const copyStart = stats ? performance.now() : 0;
      for (let i = 0; i < bucketCount; i++) {
        // RenderGroup matrices: one bulk Float32Array.set per group.
        let groupInstancesInBucket = 0;
        for (const g of groupBuckets[i]) {
          data.set(g.instanceMatrices, offset * FLOATS_PER_INSTANCE);
          offset += g.instanceCount;
          groupInstancesInBucket += g.instanceCount;
        }
        // RenderObject matrices: per-instance writeMatrices (legacy path).
        this.writeMatrices(data, buckets[i], offset);
        offset += buckets[i].length;
        this.lodVisibleCounts[i] = buckets[i].length + groupInstancesInBucket;
      }
      if (stats) stats.matrixCopyMs += performance.now() - copyStart;
      this.lodVisibleCounts.length = bucketCount;
      for (let i = 0; i < this.lodTransitions.length; i++) {
        this.lodTransitions[i].count = 0;
        this.lodTransitions[i].avgFade = 0;
      }

      const uploadStart = stats ? performance.now() : 0;
      gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
      gl.bufferSubData(gl.ARRAY_BUFFER, 0, data, 0, offset * FLOATS_PER_INSTANCE);
      if (stats) stats.uploadMs += performance.now() - uploadStart;
      return;
    }

    // Crossfade enabled: partition with transition bands.
    // Each LOD boundary has a fade band; entries in the fade band are placed
    // in the LOWER bucket and tracked separately so the same instance is drawn
    // twice (with LOD i mesh + LOD i+1 mesh) under complementary dither.
    //
    // RenderObjects partition per-instance (legacy authored statics path).
    // RenderGroups partition per-cluster (one fade decision per tile-mesh)
    // — `drawTransition` doesn't care which path the instance came from; the
    // dither pattern reads the same `lodTransitions[i].avgFade` value either
    // way. Mixed populations average their fade factors weighted by instance
    // count.
    //
    // Layout per bucket:
    //   [core RG matrices][core RO matrices][transition RG matrices][transition RO matrices]
    // The transition tail (RG + RO) sits at the bucket end so the existing
    // `drawTransition` offset math (`bucketTotal - trans.count`) keeps working.
    const coreBuckets = this.coreBuckets;
    const transitionBuckets = this.transitionBuckets;
    const transitionFadeSums = this.transitionFadeSums;
    const groupBuckets = this.groupBucketsScratch(bucketCount);
    const groupTrans = this.groupTransitionScratch(this.lodLevels.length);
    const transitionGroupBuckets = groupTrans.buckets;
    const transitionGroupFadeSums = groupTrans.fadeSums;
    for (let i = 0; i < bucketCount; i++) {
      coreBuckets[i].length = 0;
      groupBuckets[i].length = 0;
    }
    for (let i = 0; i < this.lodLevels.length; i++) {
      transitionBuckets[i].length = 0;
      transitionFadeSums[i] = 0;
      transitionGroupBuckets[i].length = 0;
      transitionGroupFadeSums[i] = 0;
    }

    // Recompute thresholds only when scale changes
    const scale = lodDistanceScale;
    const thresholds = this.cachedThresholds;
    const fadeBands = this.cachedFadeBands;
    if (scale !== this.cachedLodScale) {
      this.cachedLodScale = scale;
      for (let i = 0; i < this.lodLevels.length; i++) {
        thresholds[i] = Math.sqrt(this.lodLevels[i].maxDistanceSq) * scale;
        fadeBands[i] = thresholds[i] * this.lodLevels[i].fadeBand;
      }
    }

    const partStart = stats ? performance.now() : 0;
    const skipSqCF = this.skipDistanceSq;
    // RenderGroup partition: per-cluster crossfade by tile-center distance.
    for (const g of visibleGroups) {
      const tc = g.tileCenter;
      const dx = tc[0] - cx;
      const dy = tc[1] - cy;
      const dz = tc[2] - cz;
      const distSq = dx * dx + dy * dy + dz * dz;
      if (skipSqCF !== null && distSq >= skipSqCF * scaleSq) continue;

      let placed = false;
      for (let i = 0; i < thresholds.length; i++) {
        const t = thresholds[i];
        const fb = fadeBands[i];
        const fadeStart = t - fb;
        const fadeEnd = t + fb;

        if (distSq < fadeStart * fadeStart) {
          groupBuckets[i].push(g);
          placed = true;
          break;
        } else if (distSq < fadeEnd * fadeEnd) {
          const dist = Math.sqrt(distSq);
          const fade = (dist - fadeStart) / (2 * fb);
          transitionGroupBuckets[i].push(g);
          // Weight by instance count — a 32-instance RG should pull the
          // bucket's avgFade more than a 4-instance RG.
          transitionGroupFadeSums[i] += fade * g.instanceCount;
          placed = true;
          break;
        }
      }
      if (!placed) {
        groupBuckets[bucketCount - 1].push(g);
      }
    }
    // RenderObject partition (per-instance crossfade — legacy path).
    for (const obj of visible) {
      const pos = obj.bounds!.boundingSphere.position;
      const dx = pos[0] - cx;
      const dy = pos[1] - cy;
      const dz = pos[2] - cz;
      const distSq = dx * dx + dy * dy + dz * dz;
      if (skipSqCF !== null && distSq >= skipSqCF * scaleSq) continue;

      let placed = false;
      for (let i = 0; i < thresholds.length; i++) {
        const t = thresholds[i];
        const fb = fadeBands[i];
        const fadeStart = t - fb;
        const fadeEnd = t + fb;

        if (distSq < fadeStart * fadeStart) {
          coreBuckets[i].push(obj);
          placed = true;
          break;
        } else if (distSq < fadeEnd * fadeEnd) {
          const dist = Math.sqrt(distSq); // sqrt only for transition-zone instances
          const fade = (dist - fadeStart) / (2 * fb);
          transitionBuckets[i].push(obj);
          transitionFadeSums[i] += fade;
          placed = true;
          break;
        }
      }
      if (!placed) {
        coreBuckets[bucketCount - 1].push(obj);
      }
    }
    if (stats) stats.lodPartitionMs += performance.now() - partStart;

    // Combine RG + RO transition contributions into per-boundary {count, avgFade}.
    for (let i = 0; i < this.lodLevels.length; i++) {
      let rgInstances = 0;
      for (const g of transitionGroupBuckets[i]) rgInstances += g.instanceCount;
      const roCount = transitionBuckets[i].length;
      const totalCount = rgInstances + roCount;
      this.lodTransitions[i].count = totalCount;
      this.lodTransitions[i].avgFade =
        totalCount > 0
          ? (transitionGroupFadeSums[i] + transitionFadeSums[i]) / totalCount
          : 0;
    }

    // Layout: [core RGs][core ROs][transition RGs][transition ROs] per bucket.
    const data = this.instanceData!;
    let offset = 0;
    const copyStart = stats ? performance.now() : 0;
    for (let i = 0; i < bucketCount; i++) {
      // Core RGs
      let coreInstances = 0;
      for (const g of groupBuckets[i]) {
        data.set(g.instanceMatrices, offset * FLOATS_PER_INSTANCE);
        offset += g.instanceCount;
        coreInstances += g.instanceCount;
      }
      // Core ROs
      const core = coreBuckets[i];
      this.writeMatrices(data, core, offset);
      offset += core.length;

      // Transition RGs
      let transInstances = 0;
      if (i < this.lodLevels.length) {
        for (const g of transitionGroupBuckets[i]) {
          data.set(g.instanceMatrices, offset * FLOATS_PER_INSTANCE);
          offset += g.instanceCount;
          transInstances += g.instanceCount;
        }
      }
      // Transition ROs
      const trans = i < this.lodLevels.length ? transitionBuckets[i] : [];
      this.writeMatrices(data, trans, offset);
      offset += trans.length;

      this.lodVisibleCounts[i] = coreInstances + core.length + transInstances + trans.length;
    }
    if (stats) stats.matrixCopyMs += performance.now() - copyStart;
    this.lodVisibleCounts.length = bucketCount;

    const totalInstances = this.lodVisibleCounts.reduce((a, b) => a + b, 0);
    const uploadStart = stats ? performance.now() : 0;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, data, 0, totalInstances * FLOATS_PER_INSTANCE);
    if (stats) stats.uploadMs += performance.now() - uploadStart;
  }

  private writeMatrices(data: Float32Array, objects: RenderObject[], startIndex: number): void {
    for (let i = 0; i < objects.length; i++) {
      const matrix = objects[i].modelMatrix;
      data.set(matrix as Float32Array, (startIndex + i) * FLOATS_PER_INSTANCE);
    }
  }

  /**
   * Lazily resize {@link groupBuckets} to match the current LOD bucket count
   * and return it. Pre-allocates inner arrays so the per-frame partition can
   * `.push()` without further allocation.
   */
  private groupBucketsScratch(bucketCount: number): RenderGroup[][] {
    if (this.groupBuckets.length !== bucketCount) {
      this.groupBuckets = new Array(bucketCount);
      for (let i = 0; i < bucketCount; i++) this.groupBuckets[i] = [];
    }
    return this.groupBuckets;
  }

  /** Same lazy-resize as {@link groupBucketsScratch} but for transition (one entry per LOD boundary). */
  private groupTransitionScratch(boundaryCount: number): {
    buckets: RenderGroup[][];
    fadeSums: number[];
  } {
    if (this.groupTransitionBuckets.length !== boundaryCount) {
      this.groupTransitionBuckets = new Array(boundaryCount);
      this.groupTransitionFadeSums = new Array(boundaryCount);
      for (let i = 0; i < boundaryCount; i++) {
        this.groupTransitionBuckets[i] = [];
        this.groupTransitionFadeSums[i] = 0;
      }
    }
    return {
      buckets: this.groupTransitionBuckets,
      fadeSums: this.groupTransitionFadeSums,
    };
  }

  private rebuildVAOs(gl: WebGL2RenderingContext): void {
    // Delete old VAOs
    if (this.instanceVAO) gl.deleteVertexArray(this.instanceVAO);
    for (const vao of this.lodVAOs) gl.deleteVertexArray(vao);
    if (this.shadowMeshVAO) gl.deleteVertexArray(this.shadowMeshVAO);
    this.lodVAOs = [];
    this.shadowMeshVAO = null;

    // Rebuild
    this.instanceVAO = this.buildVAOForMesh(gl, this.mesh);
    for (const lod of this.lodLevels) {
      this.lodVAOs.push(this.buildVAOForMesh(gl, lod.mesh));
    }
    if (this.shadowMesh) {
      this.shadowMeshVAO = this.buildVAOForMesh(gl, this.shadowMesh);
    }
  }

  public get visibleCount(): number {
    let count = this.visibleMembers.length;
    for (const g of this.visibleRenderGroups) count += g.instanceCount;
    return count;
  }

  public get renderedFaceCount(): number {
    if (this.lodVisibleCounts.length === 0) return 0;
    // Each LOD level's normal draw (core + transition-out side)
    let total =
      Math.floor((this.mesh.indexBuffer?.itemCount ?? 0) / 3) * (this.lodVisibleCounts[0] ?? 0);
    for (let i = 0; i < this.lodLevels.length; i++) {
      total +=
        Math.floor((this.lodLevels[i].mesh.indexBuffer?.itemCount ?? 0) / 3) *
        (this.lodVisibleCounts[i + 1] ?? 0);
    }
    // Crossfade extra: transition instances also drawn with next LOD's mesh (fading-in side)
    for (let i = 0; i < this.lodTransitions.length && i < this.lodLevels.length; i++) {
      const tc = this.lodTransitions[i].count;
      if (tc > 0) {
        total += Math.floor((this.lodLevels[i].mesh.indexBuffer?.itemCount ?? 0) / 3) * tc;
      }
    }
    return total;
  }

  /**
   * Draw LOD 0 (base mesh) instances.
   * @param excludeTransition When true (crossfade enabled), excludes transition
   *   instances so they can be drawn separately with dither. When false, draws all.
   */
  public draw(gl: WebGL2RenderingContext, excludeTransition = false): void {
    const total = this.lodVisibleCounts[0] ?? this.visibleMembers.length;
    const transCount = excludeTransition ? (this.lodTransitions[0]?.count ?? 0) : 0;
    const count = total - transCount;
    if (count <= 0) return;
    if (this.baseDoubleSided) gl.disable(gl.CULL_FACE);
    gl.bindVertexArray(this.instanceVAO);
    // Always rebind instance attrib pointers at offset 0.  drawTransition()
    // and drawLod() modify the VAO's attrib pointers to a non-zero offset
    // and those changes persist across frames.
    this.bindInstanceAttribOffset(gl, 0);
    this.drawMesh(gl, this.mesh, count);
    if (this.baseDoubleSided) gl.enable(gl.CULL_FACE);
  }

  /**
   * Draw transition instances for boundary `boundaryIndex` (LOD i → i+1)
   * using the given VAO index and instance offset.
   * @param meshSide 0 = draw with LOD i mesh (fading out), 1 = draw with LOD i+1 mesh (fading in)
   */
  public drawTransition(
    gl: WebGL2RenderingContext,
    boundaryIndex: number,
    meshSide: 0 | 1,
  ): void {
    const trans = this.lodTransitions[boundaryIndex];
    if (!trans || trans.count === 0) return;

    // Transition instances are the LAST entries in bucket `boundaryIndex`
    const bucketTotal = this.lodVisibleCounts[boundaryIndex] ?? 0;
    let instanceOffset = 0;
    for (let i = 0; i < boundaryIndex; i++) {
      instanceOffset += this.lodVisibleCounts[i] ?? 0;
    }
    instanceOffset += bucketTotal - trans.count; // skip core, point to transition tail

    // meshSide 0: draw with the "current" LOD mesh (fading out)
    // meshSide 1: draw with the "next" LOD mesh (fading in)
    let vao: WebGLVertexArrayObject;
    let mesh: Mesh;
    let lod: LodLevel | null = null;

    if (meshSide === 0) {
      // Current LOD's mesh: boundary 0 = base mesh, boundary i = lodLevels[i-1]
      if (boundaryIndex === 0) {
        vao = this.instanceVAO!;
        mesh = this.mesh;
      } else {
        vao = this.lodVAOs[boundaryIndex - 1];
        mesh = this.lodLevels[boundaryIndex - 1].mesh;
        lod = this.lodLevels[boundaryIndex - 1];
      }
    } else {
      // Next LOD's mesh
      vao = this.lodVAOs[boundaryIndex];
      mesh = this.lodLevels[boundaryIndex].mesh;
      lod = this.lodLevels[boundaryIndex];
    }

    const isDoubleSided = lod !== null ? lod.doubleSided : this.baseDoubleSided;
    if (isDoubleSided) gl.disable(gl.CULL_FACE);

    gl.bindVertexArray(vao);
    this.bindInstanceAttribOffset(gl, instanceOffset);
    this.drawMesh(gl, mesh, trans.count);

    if (isDoubleSided) gl.enable(gl.CULL_FACE);
  }

  /**
   * Draw every visible instance using the specified LOD level's mesh,
   * ignoring the camera-distance LOD partition. Used by shadow passes
   * that want all casters to render with the simplified mesh regardless
   * of how close they are to the viewer.
   */
  public drawAllAtLod(gl: WebGL2RenderingContext, lodIndex: number): void {
    let total = this.visibleMembers.length;
    for (const g of this.visibleRenderGroups) total += g.instanceCount;
    if (total === 0) return;
    const lod = this.lodLevels[lodIndex];
    if (!lod) return;

    if (lod.doubleSided) gl.disable(gl.CULL_FACE);

    gl.bindVertexArray(this.lodVAOs[lodIndex]);
    this.bindInstanceAttribOffset(gl, 0);
    this.drawMesh(gl, lod.mesh, total);

    if (lod.doubleSided) gl.enable(gl.CULL_FACE);
  }

  /**
   * Draw every visible instance using the dedicated shadow mesh.
   * Returns false if no shadow mesh is registered; the caller should then
   * fall back to the base mesh (or another sensible default).
   */
  public drawAllAtShadowMesh(gl: WebGL2RenderingContext): boolean {
    if (!this.shadowMesh || !this.shadowMeshVAO) return false;
    let total = this.visibleMembers.length;
    for (const g of this.visibleRenderGroups) total += g.instanceCount;
    if (total === 0) return true;

    gl.bindVertexArray(this.shadowMeshVAO);
    this.bindInstanceAttribOffset(gl, 0);
    this.drawMesh(gl, this.shadowMesh, total);
    return true;
  }

  /**
   * Draw a specific LOD level's instances.
   * @param excludeTransition When true, excludes transition instances at the
   *   next boundary so they can be drawn separately with dither.
   */
  public drawLod(gl: WebGL2RenderingContext, lodIndex: number, excludeTransition = false): void {
    const total = this.lodVisibleCounts[lodIndex + 1] ?? 0;
    const nextTransCount = excludeTransition ? (this.lodTransitions[lodIndex + 1]?.count ?? 0) : 0;
    const coreCount = total - nextTransCount;
    if (coreCount <= 0) return;

    let instanceOffset = 0;
    for (let i = 0; i <= lodIndex; i++) {
      instanceOffset += this.lodVisibleCounts[i] ?? 0;
    }

    const lod = this.lodLevels[lodIndex];

    if (lod.doubleSided) gl.disable(gl.CULL_FACE);

    gl.bindVertexArray(this.lodVAOs[lodIndex]);
    this.bindInstanceAttribOffset(gl, instanceOffset);
    this.drawMesh(gl, lod.mesh, coreCount);

    if (lod.doubleSided) gl.enable(gl.CULL_FACE);
  }

  private bindInstanceAttribOffset(gl: WebGL2RenderingContext, instanceOffset: number): void {
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instanceBuffer);
    const instStride = FLOATS_PER_INSTANCE * Float32Array.BYTES_PER_ELEMENT;
    const byteOffset = instanceOffset * FLOATS_PER_INSTANCE * Float32Array.BYTES_PER_ELEMENT;
    for (let col = 0; col < 4; col++) {
      const loc = 6 + col;
      gl.vertexAttribPointer(
        loc, 4, gl.FLOAT, false, instStride,
        byteOffset + col * 4 * Float32Array.BYTES_PER_ELEMENT,
      );
    }
  }

  private drawMesh(gl: WebGL2RenderingContext, mesh: Mesh, count: number): void {
    if (mesh.indexBuffer) {
      gl.drawElementsInstanced(
        mesh.buffer.glDrawMode,
        mesh.indexBuffer.itemCount,
        mesh.indexBuffer.getIndexElementType(gl),
        0,
        count,
      );
    } else {
      gl.drawArraysInstanced(mesh.buffer.glDrawMode, 0, mesh.buffer.itemCount, count);
    }
  }

  public destroy(gl: WebGL2RenderingContext): void {
    if (this.instanceVAO) gl.deleteVertexArray(this.instanceVAO);
    for (const vao of this.lodVAOs) gl.deleteVertexArray(vao);
    if (this.shadowMeshVAO) gl.deleteVertexArray(this.shadowMeshVAO);
    if (this.instanceBuffer) gl.deleteBuffer(this.instanceBuffer);
    this.instanceVAO = null;
    this.lodVAOs = [];
    this.shadowMeshVAO = null;
    this.instanceBuffer = null;
    this.instanceData = null;
  }

  private bindAttr(
    gl: WebGL2RenderingContext,
    location: number,
    segment: MeshLayoutSegment,
    stride: number,
  ): void {
    gl.enableVertexAttribArray(location);
    gl.vertexAttribPointer(
      location,
      segment.length,
      gl.FLOAT,
      false,
      stride,
      segment.offset * Float32Array.BYTES_PER_ELEMENT,
    );
  }
}
